import '@styles/index.css';
import React, { useState, useEffect, useCallback } from 'react';
import AuthLayout from '@components/Layout/auth/AuthLayout';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import {
    setCountryList,
    setLogoError,
    setUserRole,
} from '@store/reducers/auth/signupSlice';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { OTP_PATH, USER_ROLE_PATH, LOGIN_PATH } from '@constants/routes';
import { checkUserExists, fetchCountries } from '@utils/utils';
import AccountInfo from './AccountInfo';
import SetPassword from './SetPassword';
import { TiTick } from 'react-icons/ti';
import { APPROVED, HOSPITAL, INDIVIDUAL, NURSE } from '@constants/constants';
import HospitalForm from '@components/Form/HospitalForm';
import IndividualForm from '@components/Form/IndividualForm';
import NurseForm from '@components/Form/NurseForm';
import ErrorMessage from '@components/Error/ErrorMessage';
import { checkDeviceIdExists } from '@utils/deviceUtils';
import { HOSPITAL_SIGNUP_VERIFICATION_API } from '@constants/auth/Api';
import { API_KEY } from '@constants/AppConfig';

const Signup = () => {
    const [step, setStep] = useState<number>(1);
    const userEmail = useAppSelector((state) => state.signup.email);
    const userDeviceUniqueID = useAppSelector(
        (state) => state.signup.deviceUniqueID
    );
    const userRole = useAppSelector((state) => state.signup.userRole);
    const hospitalID = useAppSelector((state) => state.signup.hospital_id);
    const logo = useAppSelector((state) => state.signup.logo_name);
    const countryList = useAppSelector((state) => state.signup.countryList);

    const [error, setError] = useState<string | null>(null);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleBeforeUnload = useCallback((event: BeforeUnloadEvent) => {
        const message = 'Are you sure you want to leave?';
        event.preventDefault();
        event.returnValue = message;
        return message;
    }, []);

    useEffect(() => {
        window.addEventListener('beforeunload', handleBeforeUnload);

        if (userRole == '') {
            navigate(LOGIN_PATH);
        }

        // Only fetch if country list is empty
        if (countryList.length === 0) {
            handleFetchCountries();
        }

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [LOGIN_PATH, userRole, navigate, handleBeforeUnload, countryList.length]);

    const handleFetchCountries = async () => {
        const list = await fetchCountries();
        if (list.countries) {
            dispatch(setCountryList(list.countries));
        } else if (list.error) {
            console.error('Error fetching countries:', list.error);
        }
    };

    function validateEmail(email: string) {
        const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return pattern.test(email);
    }

    const handleNext = async (event: React.SyntheticEvent) => {
        event.preventDefault();
        try {
            // console.log('hosID', hospitalID.toLowerCase());
            let hospital_uuid;
            if (userRole.toLowerCase() === NURSE.toLowerCase()) {
                const hospitalItem = await verifyHospitalID(
                    hospitalID.toLowerCase()
                );
                if (!hospitalItem) {
                    setError('Invalid hospital ID.');
                    return; // Stop execution if no hospital is found
                }
            }

            if (step === 1) {
                // if (userDeviceUniqueID) {
                //     try {
                //         const res =
                //             await checkDeviceIdExists(userDeviceUniqueID);
                //         if (res == 'Device ID exists') {
                //             setError(res);
                //             return;
                //         } else {
                //             setStep(step + 1);
                //             setError(null);
                //         }
                //     } catch (error: any) {
                //         setError(error);
                //     }
                // }
                if (userRole.toLowerCase() == HOSPITAL.toLowerCase() && !logo) {
                    dispatch(setLogoError(true));
                } else {
                    setStep(step + 1);
                    setError(null);
                    dispatch(setLogoError(false));
                }
            } else if (step === 2) {
                if (!validateEmail(userEmail)) {
                    setError('Invalid email');
                    return;
                }
                try {
                    await checkUserExists(userEmail, true);
                    setStep(step + 1);
                    setError(null);
                } catch (error: any) {
                    // setError(error);
                    let errorMessage = 'Internal Server Error';

                    switch (error.name) {
                        case 'UsernameExistsException':
                        case 'NotAuthorizedException':
                            errorMessage = 'Invalid credentials';
                            break;
                        case 'InvalidParameterException':
                            errorMessage = 'Invalid input parameters';
                            break;
                        case 'TooManyRequestsException':
                            errorMessage =
                                'Too many requests. Please try again later.';
                            break;
                        case 'PasswordTooShortException':
                            errorMessage = 'Password is too short';
                            break;
                        default:
                            errorMessage =
                                error.message || 'An unknown error occurred';
                    }

                    setError(errorMessage);
                }
            } else if (step === 3) {
                navigate(OTP_PATH);
            } else {
                setStep(step + 1);
                setError(null);
            }

            // Reset error state if needed
        } catch (error: any) {
            let errorMessage = 'Internal Server Error';
            switch (error.name) {
                case 'UsernameExistsException':
                case 'NotAuthorizedException':
                    errorMessage = 'Invalid credentials';
                    break;
                case 'InvalidParameterException':
                    errorMessage = 'Invalid input parameters';
                    break;
                case 'TooManyRequestsException':
                    errorMessage = 'Too many requests. Please try again later.';
                    break;
                case 'PasswordTooShortException':
                    errorMessage = 'Password is too short';
                    break;
                default:
                    errorMessage = error.message || 'An unknown error occurred';
            }
            setError(errorMessage);
        }
    };

    const handleBackClick = () => {
        if (step > 1) {
            setStep(step - 1);
        } else {
            dispatch(setUserRole(''));
            navigate(USER_ROLE_PATH);
        }
    };

    const verifyHospitalID = async (hospitalId: string) => {
        try {
            const response = await fetch(HOSPITAL_SIGNUP_VERIFICATION_API, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY,
                },
                body: JSON.stringify({
                    hospital_id: hospitalId,
                }),
            });

            const data = await response.json();

            // Status 200 means success with message "Hospital ID exists"
            if (response.status === 200 && data.message === 'Hospital ID exists') {
                return data;
            }
            
            // Status 400 means failure with message "Invalid Hospital ID"
            if (response.status === 400 && data.message === 'Invalid Hospital ID') {
                return null;
            }

            // Handle unexpected response status or message
            throw new Error(data.message || `API request failed with status ${response.status}`);
        } catch (error: any) {
            let errorMessage = 'Unexpected error';

            if (error.message) {
                errorMessage = error.message;
            } else {
                switch (error.name) {
                    case 'TypeError':
                        errorMessage = 'Network error. Please check your connection.';
                        break;
                    default:
                        errorMessage = error.message || 'Unexpected error occurred';
                }
            }

            setError(errorMessage);
            return null;
        }
    };

    return (
        <AuthLayout
            onBackClick={handleBackClick}
            className="relative min-h-screen bg-cover bg-center overflow-x-hidden flex flex-col justify-between"
            style={{
                background:
                    "linear-gradient(90deg, rgba(243, 246, 255, 0.98) 0%, rgba(243, 246, 255, 0.85) 45%, rgba(255, 255, 255, 0.15) 100%), url('/public/images/hospital_bg.png') no-repeat right center / cover",
            }}
        >
            {/* Background elements */}
            {/* Floating plus signs */}
            <div className="absolute top-[28%] left-[20%] text-blue-200 text-3xl font-extralight select-none pointer-events-none opacity-40 z-0">
                +
            </div>
            <div className="absolute bottom-[28%] left-[16%] text-blue-200 text-2xl font-extralight select-none pointer-events-none opacity-30 z-0">
                +
            </div>
            <div className="absolute top-[55%] left-[24%] text-blue-100 text-4xl font-extralight select-none pointer-events-none opacity-50 z-0">
                +
            </div>

            {/* Grid dot pattern on the top-right */}
            <svg
                className="absolute top-8 right-8 w-32 h-32 pointer-events-none opacity-[0.25] z-0"
                fill="#3b82f6"
            >
                <defs>
                    <pattern
                        id="dotGrid"
                        x="0"
                        y="0"
                        width="16"
                        height="16"
                        patternUnits="userSpaceOnUse"
                    >
                        <circle cx="2" cy="2" r="1.2" />
                    </pattern>
                </defs>
                <rect width="128" height="128" fill="url(#dotGrid)" />
            </svg>

            {/* Bottom-left line waves */}
            <svg
                className="absolute bottom-0 left-0 w-[450px] h-[250px] pointer-events-none opacity-[0.35] z-0"
                viewBox="0 0 450 250"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M-20 250 C 80 230, 120 180, 180 160 C 240 140, 300 170, 450 120"
                    stroke="#bfdbfe"
                    strokeWidth="1"
                />
                <path
                    d="M-20 250 C 60 210, 100 160, 160 140 C 220 120, 280 150, 450 100"
                    stroke="#93c5fd"
                    strokeWidth="1"
                    opacity="0.6"
                />
                <path
                    d="M-20 250 C 100 240, 140 190, 200 180 C 260 170, 320 200, 450 140"
                    stroke="#dbeafe"
                    strokeWidth="1.2"
                    opacity="0.8"
                />
                <path
                    d="M-20 250 C 40 190, 80 140, 140 120 C 200 100, 260 130, 450 80"
                    stroke="#eff6ff"
                    strokeWidth="0.8"
                    opacity="0.5"
                />
            </svg>

            {/* Bottom-right line waves */}
            <svg
                className="absolute bottom-0 right-0 w-[450px] h-[250px] pointer-events-none opacity-[0.35] z-0"
                viewBox="0 0 450 250"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M470 250 C 370 230, 330 180, 270 160 C 210 140, 150 170, 0 120"
                    stroke="#bfdbfe"
                    strokeWidth="1"
                />
                <path
                    d="M470 250 C 390 210, 350 160, 290 140 C 230 120, 170 150, 0 100"
                    stroke="#93c5fd"
                    strokeWidth="1"
                    opacity="0.6"
                />
                <path
                    d="M470 250 C 350 240, 310 190, 250 180 C 190 170, 130 200, 0 140"
                    stroke="#dbeafe"
                    strokeWidth="1.2"
                    opacity="0.8"
                />
                <path
                    d="M470 250 C 410 190, 370 140, 310 120 C 250 100, 190 130, 0 80"
                    stroke="#eff6ff"
                    strokeWidth="0.8"
                    opacity="0.5"
                />
            </svg>

            {/* Sparkles / Glitter background behind the device */}
            <svg
                className="absolute left-[1%] top-[50%] -translate-y-[50%] w-[320px] h-[580px] pointer-events-none z-0"
                viewBox="0 0 320 580"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <defs>
                    <radialGradient id="glitterGlow" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
                        <stop offset="30%" stopColor="#93c5fd" stopOpacity="0.8" />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                    </radialGradient>
                    <radialGradient id="deviceAura" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.28" />
                        <stop offset="55%" stopColor="#60a5fa" stopOpacity="0.12" />
                        <stop offset="100%" stopColor="#dbeafe" stopOpacity="0" />
                    </radialGradient>
                </defs>

                {/* Soft blue glow aura behind the device */}
                <ellipse cx="160" cy="290" rx="120" ry="220" fill="url(#deviceAura)" />

                {/* Sparkle 1 */}
                <path d="M 60 140 Q 60 150 70 150 Q 60 150 60 160 Q 60 150 50 150 Q 60 150 60 140 Z" fill="#ffffff" opacity="0.9" />
                <circle cx="60" cy="150" r="12" fill="url(#glitterGlow)" opacity="0.6" />

                {/* Sparkle 2 */}
                <path d="M 240 220 Q 240 226 246 226 Q 240 226 240 232 Q 240 226 234 226 Q 240 226 240 220 Z" fill="#ffffff" opacity="0.95" />
                <circle cx="240" cy="226" r="8" fill="url(#glitterGlow)" opacity="0.7" />

                {/* Sparkle 3 */}
                <path d="M 110 380 Q 110 388 118 388 Q 110 388 110 396 Q 110 388 102 388 Q 110 388 110 380 Z" fill="#ffffff" opacity="0.85" />
                <circle cx="110" cy="388" r="10" fill="url(#glitterGlow)" opacity="0.5" />

                {/* Sparkle 4 */}
                <path d="M 280 430 Q 280 435 285 435 Q 280 435 280 440 Q 280 435 275 435 Q 280 435 280 430 Z" fill="#ffffff" opacity="0.9" />
                <circle cx="280" cy="435" r="6" fill="url(#glitterGlow)" opacity="0.6" />

                {/* Sparkle 5 */}
                <path d="M 180 80 Q 180 85 185 85 Q 180 85 180 90 Q 180 85 175 85 Q 180 85 180 80 Z" fill="#ffffff" opacity="0.8" />
                <circle cx="180" cy="85" r="6" fill="url(#glitterGlow)" opacity="0.5" />

                {/* Sparkle 6 */}
                <path d="M 70 490 Q 70 498 78 498 Q 70 498 70 506 Q 70 498 62 498 Q 70 498 70 490 Z" fill="#ffffff" opacity="0.9" />
                <circle cx="70" cy="498" r="10" fill="url(#glitterGlow)" opacity="0.6" />

                {/* Tiny glitter dots */}
                <circle cx="45" cy="200" r="2" fill="#ffffff" opacity="0.8" />
                <circle cx="130" cy="180" r="1.5" fill="#93c5fd" opacity="0.9" />
                <circle cx="80" cy="280" r="2.5" fill="#ffffff" opacity="0.75" />
                <circle cx="210" cy="320" r="2" fill="#93c5fd" opacity="0.85" />
                <circle cx="250" cy="110" r="1.5" fill="#ffffff" opacity="0.9" />
                <circle cx="160" cy="450" r="3" fill="#ffffff" opacity="0.7" />
                <circle cx="160" cy="450" r="8" fill="url(#glitterGlow)" opacity="0.4" />
                <circle cx="200" cy="410" r="2" fill="#93c5fd" opacity="0.8" />
                <circle cx="90" cy="420" r="1.5" fill="#ffffff" opacity="0.95" />
                <circle cx="240" cy="500" r="2" fill="#ffffff" opacity="0.8" />
                <circle cx="140" cy="520" r="2" fill="#93c5fd" opacity="0.9" />
            </svg>

            {/* Semi-transparent DripSet device on the left */}
            <div
                className="absolute left-[3%] top-[50%] -translate-y-[50%] w-[260px] h-[520px] bg-contain bg-no-repeat bg-center pointer-events-none opacity-[0.50] z-0"
                style={{ backgroundImage: "url('/public/images/dripset.png')" }}
            />

            {/* Main Content Area */}
            <div className="flex-grow flex flex-col justify-center py-2 px-4 z-10 w-full relative">
                <div className="flex justify-center items-center">
                    <h5 className="text-xl font-bold text-nero">
                        {t('auth.signup.create_account')}
                    </h5>
                </div>
                <div className="flex justify-between items-center mt-2 max-w-sm relative w-3/4 mx-auto">
                    <div className="step-container flex items-center">
                        <div
                            className={`step flex flex-col items-center relative z-20 ${step >= 1 ? 'completed' : ''}`}
                        >
                            <div
                                className={`w-7 h-7 !bg-circle rounded-full flex justify-center items-center font-bold text-sm mb-1 ${step >= 1 ? '!bg-circle text-white !border-circle' : ''}`}
                            >
                                {step >= 1 && <TiTick />}
                            </div>
                            <div className="label text-circle font-bold text-xs">
                                {t('auth.signup.personal_info')}
                            </div>
                        </div>
                        <div className={`bar ${step >= 2 ? 'completed' : ''}`} />
                    </div>

                    <div className="step-container flex items-center">
                        <div
                            className={`step step-second-child flex flex-col items-center relative z-10 ${step >= 2 ? 'completed' : ''}`}
                        >
                            <div
                                className={`w-7 h-7 bg-circle_inactive rounded-full flex justify-center items-center font-bold text-sm mb-1 ${step >= 2 ? '!bg-circle text-white !border-circle' : ''}`}
                            >
                                {step >= 2 && <TiTick />}
                            </div>
                            <div className="label text-circle font-bold text-xs">
                                {t('auth.signup.account_info')}
                            </div>
                        </div>
                        <div className={`bar ${step >= 3 ? 'completed' : ''}`} />
                    </div>

                    <div className="step-container flex items-center">
                        <div
                            className={`step flex flex-col items-center relative ${step >= 3 ? 'completed' : ''}`}
                        >
                            <div
                                className={`w-7 h-7 bg-circle_inactive rounded-full flex justify-center items-center font-bold text-sm mb-1 ${step >= 3 ? '!bg-circle text-white !border-circle' : ''}`}
                            >
                                {step >= 3 && <TiTick />}
                            </div>
                            <div className="label text-circle font-bold text-xs">
                                {t('auth.signup.set_password')}
                            </div>
                        </div>
                    </div>
                </div>
                <div className={`mt-3 mx-auto w-full ${step != 1 ? 'max-w-[470px]' : 'max-w-[760px]'} relative`}>
                    <div className="card shadow-xl border-gray-100 bg-white rounded-2xl">
                        <div className="signup-card-body-scroll card-body overflow-y-auto p-4">
                            {error && <ErrorMessage message={error} />}

                            <form onSubmit={handleNext}>
                                <div className="min-h-[160px]">
                                    {step === 1 && userRole == INDIVIDUAL && (
                                        <IndividualForm />
                                    )}
                                    {step === 1 && userRole == NURSE && (
                                        <NurseForm />
                                    )}
                                    {step === 1 && userRole == HOSPITAL && (
                                        <HospitalForm />
                                    )}
                                    {step === 2 && <AccountInfo />}
                                    {step === 3 && <SetPassword />}
                                </div>
                                <div className="w-2/4 mx-auto mt-4 z-20">
                                    <button
                                        type="submit"
                                        className="w-full text-white !bg-primary hover:bg-blue-800 transition-all duration-300 font-bold rounded-lg text-base px-5 py-2.5 text-center shadow-md"
                                    >
                                        {step === 3
                                            ? t('auth.signup.submit')
                                            : t('auth.signup.next')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </AuthLayout>
    );
};

export default Signup;
